import { useEffect } from 'react';
import { Avatar } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Delete, Edit } from '@mui/icons-material';
import Table, { TableActions } from 'components/Table';
import PageContainer from 'components/PageContainer';

import {
  ADMIN_USER_ADD,
  createAdminUserEditRoute,
} from 'adminConstants/routes';
import AlertDialog from 'components/AlertDialog';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetAdminUsers } from 'hooks/data/adminUser/useAdminUsers';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import { useMutateAdminUser } from 'hooks/data/adminUser/useAdminUser';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import { Email } from '@mui/icons-material';
import SearchField from 'further-ui/components/SearchField';

const useStyles = makeStyles()(() => ({
  dFlex: {
    display: 'flex',
  },
  marginFirstName: {
    margin: '10px 0px 0px 10px',
  },
}));

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Users', isActive: true },
];

const AdminUser = () => {
  const { classes } = useStyles();
  const deleteDisclosure = useDisclosure<{ _id: string }>();
  const [createPermission, updatePermission, deletePermission] =
    useHasPermissions([
      RolePermission.CreateUsers,
      RolePermission.EditUsers,
      RolePermission.DeleteUsers,
    ]);

  const {
    page,
    rowsPerPage,
    order,
    orderBy,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    toFirstPage,
  } = usePagination({ id: 'users-table', orderBy: 'email' });

  const { filters, handleChangeFilters } = useFilters('users-table', {
    onFiltersChange: toFirstPage,
    defaultFilters: { search: '' },
  });

  const { data: users, isFetching } = useGetAdminUsers({
    page: page === 0 ? '1' : page.toString(),
    perPage: rowsPerPage.toString(),
    type: order,
    keyName: orderBy,
    search: filters.search,
  });
  const { remove, requestPasswordResetForUser } = useMutateAdminUser();

  useEffect(() => {
    deleteDisclosure.onClose();
  }, [remove.isSuccess]);

  const handleConfirmDelete = async () => {
    remove.mutate(deleteDisclosure.actionProps?._id, {
      onSuccess: () => {
        deleteDisclosure.onClose();
      },
    });
  };

  const columns = [
    {
      label: 'First name',
      key: 'firstName',
      render: (elm) => (
        <div className={classes.dFlex}>
          <Avatar src={elm?.profilePic} />
          <div className={classes.marginFirstName}>{elm?.firstName}</div>
        </div>
      ),
    },
    { label: 'Last name', key: 'lastName' },
    { label: 'Email', key: 'email' },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              color: 'primary',
              link: createAdminUserEditRoute(elm._id),
              visible: updatePermission,
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              onClick: () => deleteDisclosure.stageAction({ _id: elm._id }),
              visible: deletePermission,
            },
            {
              label: 'Send password reset email',
              icon: Email,
              color: 'primary',
              onClick: () =>
                requestPasswordResetForUser.mutate({ id: elm._id }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageContainer heading="Admin Users" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters
          pageId="admin-users-table"
          buttons={[
            {
              visible: createPermission,
              label: 'Add User',
              link: ADMIN_USER_ADD,
            },
          ]}
        >
          <Filter>
            <SearchField
              name="search"
              placeholder="Search by name or email"
              fullWidth
              value={filters.search}
              allowEmptyQuery
              autoSearchOnDebounce
              onSearch={(search) => {
                handleChangeFilters({ search });
              }}
            />
          </Filter>
        </PageFilters>

        <Table
          columns={columns}
          onRequestSort={handleRequestSort}
          order={order}
          orderBy={orderBy}
          tablebody={users?.result ?? []}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          count={users?.total}
          pagination={true}
          loading={isFetching}
          variant="nohover"
        />
      </PageContent>

      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleConfirmDelete}
        title={`Are you sure you wish to delete this user?`}
        content={
          <p>
            This cannot be undone and all information relating to the user type
            will be lost.
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete user',
        }}
        confirmBtnProps={{ disabled: remove.isPending }}
        cancelBtnProps={{ disabled: remove.isPending }}
      />
    </PageContainer>
  );
};

export default AdminUser;
